import {
  Alert,
  Button,
  FileDropField,
  FileList,
  Stack,
  Title
} from '@efast_public/fjd-component-library';
import { ReactElement, useEffect, useState } from 'react';
import { logoState, LogoVO } from '../../../../api/hooks/useLogo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import {
  base64ToBytes,
  fileToLogoV0
} from '../../../../shared/utils/fileUtils';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

export const validateLogo = (files: Array<File>) => {
  const errors: string[] = [];
  if (files.length > 1) {
    errors.push('Nur ein einziges Logo ist zulässig.');
  }

  const file: File = files[0];

  if (
    file.type !== 'image/png' &&
    file.type !== 'image/jpeg' &&
    file.type !== 'image/svg+xml'
  ) {
    errors.push(
      'Ungültiges Dateiformat. Es sind nur Dateien im Format PNG, JPG oder SVG zulässig.'
    );
  }

  if (file.size > 20000000) {
    errors.push('Es sind nur Bilder bis maximal 20 MB zulässig.');
  }
  return errors;
};

export default function LogoSettings(): ReactElement {
  const { t } = useTranslation();
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const queryClient = useQueryClient();
  const [, setShouldRefetch] = useRecoilState(logoState);
  const [droppedFile, setDroppedFile] = useState<File | undefined>(undefined);

  const { data: logoVO, isError: isLogoError } = useQuery<LogoVO | null>({
    queryKey: [BehApiRoutes.logo()],
    queryFn: () =>
      behClient
        .get<LogoVO>(BehApiRoutes.logo())
        .then((res) => res.data)
        .catch((error) => {
          if (error?.response?.status === 404) {
            return null;
          }
          throw error;
        })
  });
  const { mutateAsync: deleteLogo, isPending: isDeleting } = useMutation({
    mutationFn: () =>
      notifyUserFromRequest(() => behClient.delete(BehApiRoutes.logo()), {
        successMessage: t('api.success.deleteLogo'),
        errorMessage: t('api.error.deleteLogo')
      }),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: [BehApiRoutes.logo()]
      });
      setShouldRefetch(true);
      setDroppedFile(undefined);
    }
  });

  const { mutateAsync: updateLogo, isPending: isUpdating } = useMutation({
    mutationFn: ({ payload }: { payload: LogoVO }) =>
      notifyUserFromRequest(() => behClient.put(BehApiRoutes.logo(), payload), {
        successMessage: t('api.success.updateLogo'),
        errorMessage: t('api.error.updateLogo')
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.logo()]
      });
    }
  });
  useEffect(() => {
    if (logoVO) {
      setDroppedFile(
        new File([base64ToBytes(logoVO.base64Data)], logoVO.fileName, {
          type: logoVO.fileType
        })
      );
    }
  }, [logoVO]);

  const removeItem = () => {
    setDroppedFile(undefined);
  };

  return (
    <div style={{ width: '50rem' }}>
      <Stack direction="column" spacing="l">
        <Title level={2} visualLevel={4}>
          Laden Sie ein individuelles Logo oder Wappen für Ihren Behörden-Client
          hoch
        </Title>
        <div className="descriptionText">
          Sobald Sie eine Datei ausgewählt und gespeichert, haben wird sie im
          Header neben dem Behörden-Client-Logo angezeigt.
          <br /> Sie können das Logo jederzeit wieder entfernen oder ändern.
          Erlaubte Formate: JPG, PNG,SVG.
          <br />
          Empfehlung der Dateigröße: &lt; 1MB
        </div>

        {isLogoError && (
          <Alert
            variant="error"
            message={t('logoSettings.error.loadFailed')}
            data-testid="logo-settings-error-alert"
          />
        )}

        <Stack alignItems="stretch" spacing="xs">
          <FileDropField
            data-testid={'logoSettingsFileDrop'}
            label="Logo auswählen"
            disabled={isUpdating || isDeleting}
            dropHelperText="oder in dieses Feld ziehen"
            onAdd={(files) => {
              const errors = validateLogo(files);
              if (errors.length > 0) {
                notifyUser({ intent: 'error', title: errors.at(0) ?? '' });
                return;
              } else {
                setDroppedFile(files[0]);
              }
            }}
          />

          {droppedFile && (
            <FileList data-testid="logoSettingsFileList">
              <FileList.FileItem
                file={droppedFile}
                onDelete={() => removeItem()}
              />
            </FileList>
          )}
        </Stack>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack direction="row" spacing="s">
            <Button
              variant="primary"
              data-testid="logoSettingsPrimaryButtonSaveLogo"
              onClick={() => {
                if (droppedFile) {
                  fileToLogoV0(droppedFile).then((file) => {
                    updateLogo({ payload: file }).catch(() => {});
                  });
                } else if (droppedFile === undefined && logoVO) {
                  deleteLogo().catch(() => {});
                }
              }}
              disabled={isUpdating || isDeleting || (!droppedFile && !logoVO)}
            >
              Logo speichern
            </Button>
          </Stack>
        </div>
      </Stack>
    </div>
  );
}
